import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, CardMedia, Stack, Typography } from '@mui/material'
import type { FC } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { OwnershipDto } from '@hcr/api/consumer'
import { Alert, COLORS, ICONS, unit } from '@hcr/ui'

import { SvgPercentageStamp } from '../../../../../../assets/images'
import { useUserDetails } from '../../../../../../contexts'
import { useBenefitsQuery } from '../../../../../../hooks/api/optimizely/queries'

import { BenefitsLoading } from './BenefitsLoading'
import { createBenefitsGroup } from './common/benefits-group/createBenefitsGroup'

const MAX_VISIBLE_BENEFIT_ITEMS = 5

const benefitsGroup = createBenefitsGroup()

export const Benefits: FC = () => {
  const [isCollapsed, setCollapsed] = useState<boolean>(false)

  const { t } = useTranslation()
  const userDetails = useUserDetails()

  const { is_villas, is_active_gold, is_b2b_gold, is_b2b_villas } = (userDetails.data?.ownership as OwnershipDto) || {}
  const isVillasOwnerNoBenefit = Boolean(is_villas) && Boolean(!userDetails.data?.has_benefits)

  const benefits = useBenefitsQuery(
    {
      isGoldOwner: (Boolean(is_active_gold) || Boolean(is_b2b_gold)) && Boolean(userDetails.data?.has_benefits),
      isVillasOwner: (Boolean(is_villas) || Boolean(is_b2b_villas)) && Boolean(userDetails.data?.has_benefits),
      isVillasOwnerNoBenefit,
    },
    { enabled: userDetails.isSuccess }
  )

  if (userDetails.isError || benefits.isError) {
    return <Alert severity='error' title={t('error.error')} description={t('error.generic')} />
  }

  if (userDetails.isSuccess && benefits.isSuccess) {
    const visibleGroups = isCollapsed
      ? benefits.data.content
      : benefits.data.content.reduce<{ groups: typeof benefits.data.content; remaining: number }>(
          (acc, group) => {
            const content = group.content.slice(0, acc.remaining)
            return content.length > 0
              ? { remaining: acc.remaining - content.length, groups: [...acc.groups, { ...group, content }] }
              : acc
          },
          { remaining: MAX_VISIBLE_BENEFIT_ITEMS, groups: [] }
        ).groups

    return (
      <Stack position='relative' border={`${unit(0.25)} solid ${COLORS.vaalea[700]}`}>
        <CardMedia image={benefits.data.imageUrl} sx={{ width: '100%', height: unit(44.5), display: 'block' }} />
        <Box position='absolute' top={unit(2.75)} right={unit(2.75)}>
          <SvgPercentageStamp />
        </Box>
        <Stack direction='column' gap={unit(2.5)} paddingTop={unit(4.5)} paddingBottom={unit(6)} paddingX={unit(4)}>
          <Typography variant='headlineM'>{benefits.data.title}</Typography>
          <Typography variant='bodyM'>{benefits.data.description}</Typography>
        </Stack>

        {visibleGroups.map(benefitsGroup)}

        <Box
          margin={unit(3.5)}
          paddingTop={unit(2.5)}
          borderTop={`${unit(0.25)} dashed ${COLORS.vaalea[800]}`}
          sx={{ cursor: 'pointer' }}
          onClick={() => setCollapsed(!isCollapsed)}
        >
          <Stack
            direction='row'
            gap={unit(2.5)}
            justifyContent='center'
            alignItems='center'
            paddingTop={unit(2.75)}
            paddingRight={unit(12.5)}
            paddingBottom={unit(3.25)}
            paddingLeft={unit(11.75)}
            onClick={() => setCollapsed(!isCollapsed)}
          >
            <Typography
              variant='labelM'
              color={COLORS.burgundi[500]}
              sx={{ textDecoration: 'underline', lineHeight: unit(5) }}
            >
              {isCollapsed ? t('owner.show-less') : t('owner.show-more')}
            </Typography>
            <FontAwesomeIcon
              size={'sm'}
              icon={isCollapsed ? ICONS.fasArrowUp : ICONS.fasArrowDown}
              color={COLORS.burgundi[500]}
            />
          </Stack>
        </Box>
      </Stack>
    )
  }

  return <BenefitsLoading />
}
